// Production security utilities and configurations

// Content Security Policy configuration
export const generateCSP = (nonce?: string) => {
  const policies = {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      "'unsafe-inline'", // Required for React development, remove in production
      nonce ? `'nonce-${nonce}'` : null,
      // Add your analytics/monitoring domains
      'https://www.google-analytics.com',
      'https://www.googletagmanager.com',
    ].filter(Boolean),
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Required for styled-components and CSS-in-JS
      'https://fonts.googleapis.com',
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com',
      'data:', // For base64 encoded fonts
    ],
    'img-src': [
      "'self'",
      'data:', // For base64 images
      'blob:', // For file uploads/previews
      'https:', // Allow HTTPS images
      // Add your CDN domains
    ],
    'connect-src': [
      "'self'",
      // Add your API endpoints
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
      'wss:', // WebSocket connections
      'https:', // HTTPS API calls
    ],
    'frame-src': ["'none'"], // Prevent embedding in frames
    'object-src': ["'none'"], // Prevent object/embed/applet
    'base-uri': ["'self'"], // Restrict base tag
    'form-action': ["'self'"], // Restrict form submissions
    'frame-ancestors': ["'none'"], // Prevent clickjacking
    'upgrade-insecure-requests': [], // Force HTTPS
  };

  return Object.entries(policies)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
};

// Security headers configuration
export const securityHeaders = {
  // Content Security Policy
  'Content-Security-Policy': generateCSP(),
  
  // XSS Protection
  'X-XSS-Protection': '1; mode=block',
  
  // Prevent MIME type sniffing
  'X-Content-Type-Options': 'nosniff',
  
  // Clickjacking protection
  'X-Frame-Options': 'DENY',
  
  // Referrer policy
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  
  // HTTPS enforcement (HSTS)
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  
  // Permissions policy (formerly Feature Policy)
  'Permissions-Policy': [
    'camera=()',
    'microphone=()',
    'geolocation=()',
    'payment=()',
    'usb=()',
    'magnetometer=()',
    'accelerometer=()',
    'gyroscope=()',
  ].join(', '),
  
  // Cross-Origin policies
  'Cross-Origin-Embedder-Policy': 'require-corp',
  'Cross-Origin-Opener-Policy': 'same-origin',
  'Cross-Origin-Resource-Policy': 'same-origin',
};

// Input sanitization utilities
export const sanitizeHtml = (input: string): string => {
  // Basic HTML sanitization - in production, use DOMPurify
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

export const sanitizeUrl = (url: string): string => {
  // Prevent javascript: and data: URL schemes
  const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
  
  try {
    const urlObj = new URL(url);
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return '#';
    }
    return url;
  } catch {
    return '#';
  }
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const validatePassword = (password: string): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Rate limiting utilities (client-side)
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  
  constructor(
    private maxRequests: number = 100,
    private windowMs: number = 60000 // 1 minute
  ) {}
  
  isAllowed(key: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(key) || [];
    
    // Remove old requests outside the window
    const validRequests = requests.filter(
      (timestamp) => now - timestamp < this.windowMs
    );
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(key, validRequests);
    
    return true;
  }
  
  getRemainingRequests(key: string): number {
    const requests = this.requests.get(key) || [];
    return Math.max(0, this.maxRequests - requests.length);
  }
}

export const rateLimiter = new RateLimiter();

// Secure session management
export const secureStorage = {
  set: (key: string, value: string, expirationMs?: number) => {
    const item = {
      value,
      timestamp: Date.now(),
      expiration: expirationMs ? Date.now() + expirationMs : null,
    };
    
    try {
      sessionStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to store item in sessionStorage:', error);
    }
  },
  
  get: (key: string): string | null => {
    try {
      const item = sessionStorage.getItem(key);
      if (!item) return null;
      
      const parsed = JSON.parse(item);
      
      // Check expiration
      if (parsed.expiration && Date.now() > parsed.expiration) {
        sessionStorage.removeItem(key);
        return null;
      }
      
      return parsed.value;
    } catch (error) {
      console.warn('Failed to retrieve item from sessionStorage:', error);
      return null;
    }
  },
  
  remove: (key: string) => {
    sessionStorage.removeItem(key);
  },
  
  clear: () => {
    sessionStorage.clear();
  },
};

// Environment validation
export const validateEnvironment = () => {
  const requiredEnvVars = [
    'VITE_API_BASE_URL',
    'VITE_APP_VERSION',
  ];
  
  const missing = requiredEnvVars.filter(
    (varName) => !import.meta.env[varName]
  );
  
  if (missing.length > 0) {
    console.error('Missing required environment variables:', missing);
    return false;
  }
  
  // Validate HTTPS in production
  if (import.meta.env.PROD && location.protocol !== 'https:') {
    console.error('HTTPS is required in production');
    return false;
  }
  
  return true;
};

// CSRF token management
export const csrfToken = {
  get: (): string | null => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || null;
  },
  
  set: (token: string) => {
    let meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'csrf-token';
      document.head.appendChild(meta);
    }
    meta.content = token;
  },
};

// Secure random string generation
export const generateSecureRandom = (length: number = 32): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  
  return Array.from(values, (value) => charset[value % charset.length]).join('');
};

// Content validation for rich text
export const validateRichTextContent = (content: string): {
  isValid: boolean;
  sanitized: string;
  warnings: string[];
} => {
  const warnings: string[] = [];
  let sanitized = content;
  
  // Check for potential XSS vectors
  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /data:text\/html/gi,
    /<iframe\b[^>]*>/gi,
    /<object\b[^>]*>/gi,
    /<embed\b[^>]*>/gi,
  ];
  
  dangerousPatterns.forEach((pattern, index) => {
    if (pattern.test(content)) {
      warnings.push(`Potentially dangerous content detected (pattern ${index + 1})`);
      sanitized = sanitized.replace(pattern, '');
    }
  });
  
  // Length validation
  const maxLength = 50000; // 50KB
  if (content.length > maxLength) {
    warnings.push(`Content exceeds maximum length of ${maxLength} characters`);
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return {
    isValid: warnings.length === 0,
    sanitized,
    warnings,
  };
};

// Initialize security measures
export const initializeSecurity = () => {
  // Validate environment
  if (!validateEnvironment()) {
    throw new Error('Environment validation failed');
  }
  
  // Disable right-click in production (optional)
  if (import.meta.env.PROD) {
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
    
    // Disable F12, Ctrl+Shift+I, etc. (optional, can be bypassed)
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'C') ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
      }
    });
  }
  
  // Log security initialization
  console.log('Security measures initialized');
}; 